import type {OnboardingPrompt, OnboardingPromptIcon, OnboardingPromptsPayload} from '@github-ui/code-view-types'
import type {Icon} from '@primer/octicons-react'
import {CodespacesIcon, CopilotIcon, PeopleIcon, PersonAddIcon} from '@primer/octicons-react'
import {LinkButton, Stack} from '@primer/react'
import {Card} from '@primer/react/experimental'

import styles from './OnboardingPrompts.module.css'

export interface OnboardingPromptsProps {
  prompts?: OnboardingPromptsPayload
}

/**
 * Top-of-page two-up grid shown to repo admins on an empty repo.
 *
 * All variant selection (Copilot eligibility, Codespaces flag, org/user invite
 * CTAs, MFR state, etc.) is owned by the backend and arrives pre-resolved on
 * `LayoutPayload.onboardingPrompts`. This component is presentation-only.
 */
export function OnboardingPrompts({prompts}: OnboardingPromptsProps) {
  if (!prompts) return null
  const {primary, secondary} = prompts
  if (!primary && !secondary) return null

  return (
    <Stack
      direction={{narrow: 'vertical', regular: 'horizontal'}}
      gap="normal"
      align="stretch"
      className={styles.PromptsGrid}
    >
      {primary && (
        <Stack.Item grow className={styles.PromptItem}>
          <PromptCard prompt={primary} />
        </Stack.Item>
      )}
      {secondary && (
        <Stack.Item grow className={styles.PromptItem}>
          <PromptCard prompt={secondary} />
        </Stack.Item>
      )}
    </Stack>
  )
}

const ICONS: Record<OnboardingPromptIcon, Icon> = {
  copilot: CopilotIcon,
  codespaces: CodespacesIcon,
  people: PeopleIcon,
  'person-add': PersonAddIcon,
}

function PromptCard({prompt}: {prompt: OnboardingPrompt}) {
  return (
    <Card className={styles.Prompt}>
      <Card.Icon icon={ICONS[prompt.icon]} />
      <Card.Heading>{prompt.title}</Card.Heading>
      <Card.Description>{prompt.description}</Card.Description>
      <Card.Metadata>
        <LinkButton
          href={prompt.href}
          {...(prompt.analytics && {'data-analytics-event': JSON.stringify(prompt.analytics)})}
        >
          {prompt.buttonLabel}
        </LinkButton>
      </Card.Metadata>
    </Card>
  )
}
